import { CareerPath } from '@backrs_ui/common-ui';

import {
  CareerPathSearchCategory,
  CareerPathSearchResult,
} from '@backrs_ui/grow-ui/src/models/models';

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '../baseQuery';
import { selectIsSharedSessionActive } from '../sharedSession/sharedSession';
import type { RootState } from '../store';

export const careerPathApi = createApi({
  reducerPath: 'careerPathApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['CareerPathSearch'],
  endpoints: (build) => ({
    searchCareerPathContent: build.query<
      CareerPathSearchResult,
      {
        search_term: string;
        page?: number;
        perPage?: number;
        types?: CareerPathSearchCategory[];
        sectionName: string;
      }
    >({
      query: ({ search_term, types, page = 1, perPage = 12 }) => {
        const filter = JSON.stringify({
          q: search_term,
          ...((types?.length || 0) > 0 && { types }),
        });

        const sort = JSON.stringify(['title', 'ASC']);

        const searchParams = new URLSearchParams();

        searchParams.append('filter', filter);
        searchParams.append('sort', sort);
        searchParams.append('page', String(page));
        searchParams.append('perPage', String(perPage));

        return `content-search?${searchParams.toString()}`;
      },
      providesTags: (_result, _error, { sectionName }) => [
        { type: 'CareerPathSearch', id: sectionName },
      ],
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getCareerPathById: build.query<CareerPath, string>({
      queryFn: async (id, queryApi, _extraOptions, baseQuery): Promise<{ data: CareerPath }> => {
        const state = queryApi.getState() as RootState;
        const isSharedSession: boolean = selectIsSharedSessionActive(state);

        const url: string = isSharedSession
          ? `shared/solution-session/career-path/${id}`
          : `career-path/${id}`;

        return baseQuery({ url }) as Promise<{ data: CareerPath }>;
      },
      keepUnusedDataFor: 60 * 60 * 24,
    }),
  }),
});

export const {
  useSearchCareerPathContentQuery,
  useLazySearchCareerPathContentQuery,
  useGetCareerPathByIdQuery,
  usePrefetch: usePrefetchCareerPath,
  util: { resetApiState },
} = careerPathApi;
